import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, FileUp, FileText, X, Home, Pin } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { authApi } from "../../lib/auth";
import { API } from "../../lib/site";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Link } from "react-router-dom";

const CATEGORIES = ["White Paper", "Product", "Announcement", "Materials Science", "Press"];
const EMPTY = { title: "", category: "Product", excerpt: "", body: "", date: new Date().toISOString().slice(0, 10), read_time: "5 min" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const load = useCallback(async () => {
    const { data } = await authApi.get("/news");
    setPosts(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setFile(null); setShowEditor(true); };
  const openEdit = (p) => {
    setForm({ title: p.title, category: p.category, excerpt: p.excerpt, body: p.body || "", date: p.date, read_time: p.read_time });
    setEditingId(p.id); setFile(null); setShowEditor(true);
  };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let post;
      if (editingId) {
        ({ data: post } = await authApi.put(`/news/${editingId}`, form));
      } else {
        ({ data: post } = await authApi.post(`/news`, form));
      }
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await authApi.post(`/news/${post.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editingId ? "Post updated" : "Post published");
      setShowEditor(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await authApi.delete(`/news/${id}`);
      toast.success("Post deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const removePdf = async (id) => {
    try {
      await authApi.delete(`/news/${id}/pdf`);
      toast.success("Document removed");
      await load();
    } catch {
      toast.error("Failed to remove document");
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-panel sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <p className="overline">Newsroom CMS</p>
            <h1 className="font-heading text-lg font-bold uppercase tracking-tight text-textmain leading-none">Ascendance Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="btn-ghost-tactical !px-4 !py-2 !text-xs">
              <Home size={14} /> View Site
            </Link>
            <span className="hidden sm:block text-xs text-textmuted">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="btn-ghost-tactical !px-4 !py-2 !text-xs">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-textmain">Posts</h2>
            <p className="text-sm text-textmuted mt-1">{posts.length} total · White Papers are auto-pinned to the top of the public newsroom.</p>
          </div>
          <button onClick={openNew} data-testid="new-post-btn" className="btn-tactical group">
            <Plus size={18} /> New Post
          </button>
        </div>

        {/* List */}
        <div className="border-t border-white/10" data-testid="admin-post-list">
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-post-${p.id}`} className="grid grid-cols-12 gap-4 items-center py-4 border-b border-white/10 hover:bg-white/[0.02] px-2">
              <div className="col-span-12 md:col-span-6">
                <div className="flex items-center gap-2">
                  {p.pinned && <Pin size={14} className="text-steel shrink-0" />}
                  <span className="font-heading text-base font-semibold uppercase tracking-tight text-textmain">{p.title}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {p.has_pdf && <span className="text-[10px] uppercase tracking-widest text-steel flex items-center gap-1"><FileText size={11} /> PDF</span>}
                </div>
              </div>
              <div className="col-span-4 md:col-span-2"><span className="text-xs uppercase tracking-widest text-steel border border-steel/30 px-2 py-1">{p.category}</span></div>
              <div className="col-span-4 md:col-span-2 text-xs text-textmuted">{p.date}</div>
              <div className="col-span-4 md:col-span-2 flex justify-end gap-2">
                {p.has_pdf && (
                  <button onClick={() => removePdf(p.id)} title="Remove PDF" data-testid={`remove-pdf-${p.id}`} className="p-2 text-textmuted hover:text-steel transition-colors"><FileUp size={16} className="rotate-180" /></button>
                )}
                <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`} className="p-2 text-textmuted hover:text-steel transition-colors"><Pencil size={16} /></button>
                <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.id}`} className="p-2 text-textmuted hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Editor slide-over */}
      {showEditor && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="post-editor">
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setShowEditor(false)} />
          <div className="relative w-full max-w-xl bg-panel border-l border-white/10 h-full overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-panel">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-textmain">{editingId ? "Edit Post" : "New Post"}</h3>
              <button onClick={() => setShowEditor(false)} data-testid="close-editor" className="text-textmuted hover:text-textmain"><X size={22} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-6">
              <div>
                <label className="overline block mb-3">Title</label>
                <input required value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="post-title" className="tactical-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-3">Category</label>
                  <Select value={form.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger data-testid="post-category" className="tactical-input h-auto py-3"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-white/10 rounded-none">
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`cat-${c}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.category === "White Paper" && <p className="text-[11px] text-steel mt-2 flex items-center gap-1"><Pin size={11} /> Auto-pinned to top</p>}
                </div>
                <div>
                  <label className="overline block mb-3">Date</label>
                  <input type="date" required value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="post-date" className="tactical-input" />
                </div>
              </div>
              <div>
                <label className="overline block mb-3">Read Time</label>
                <input value={form.read_time} onChange={(e) => update("read_time", e.target.value)} data-testid="post-readtime" className="tactical-input" placeholder="5 min" />
              </div>
              <div>
                <label className="overline block mb-3">Excerpt / Summary</label>
                <textarea rows={2} required value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} data-testid="post-excerpt" className="tactical-input resize-none" />
              </div>
              <div>
                <label className="overline block mb-3">Full Article Body</label>
                <textarea rows={8} value={form.body} onChange={(e) => update("body", e.target.value)} data-testid="post-body" className="tactical-input resize-none" placeholder="Full article shown on the post's detail page..." />
              </div>
              <div>
                <label className="overline block mb-3">Attach PDF (white paper / document)</label>
                <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="post-pdf" className="block w-full text-sm text-textmuted file:mr-4 file:py-2 file:px-4 file:border file:border-steel/40 file:bg-transparent file:text-steel file:uppercase file:text-xs file:tracking-widest file:cursor-pointer" />
                {file && <p className="text-xs text-steel mt-2">{file.name}</p>}
              </div>
              <button type="submit" disabled={saving} data-testid="save-post" className="btn-tactical group w-full disabled:opacity-60">
                {saving ? "Saving..." : editingId ? "Update Post" : "Publish Post"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
